import { useMemo } from "react";
import { carrierAdapter } from "src/domain/SegmentScreen/CarrierSection/carrierAdapter";
import { useTravelSearchSettings } from "src/domain/SegmentScreen/TravelSearchSettingsProvider";
import { destinationPlaceFromSegment } from "src/utils/adapters/place";
import { transitModeFromSegment } from "src/utils/adapters/transitMode";
import { useIsHotelsUrlDeeplink } from "src/utils/hooks/useNavigateToHotelsPage";
import { useRouteIndex } from "src/utils/hooks/useRoute";
import useSearch from "src/utils/hooks/useSearch";
import { useRouteSegmentIndex } from "src/utils/hooks/useSegment";
import type { Mode } from "src/utils/types/mode";

type RouteSegmentScreen = {
  screenType: "route" | "segment";
  screenModes: Mode[];
  carriers: string[];
  travelPeriods: string[];
  routeDestinations?: string[];
};

type SerpHotelScreen = {
  screenType: "hotel" | "serp";
};

export type ScreenTargeting = RouteSegmentScreen | SerpHotelScreen;

export function useScreenTargeting(): ScreenTargeting {
  const { searchResponse } = useSearch();
  const routeIndex = useRouteIndex();
  const routeSegmentIndex = useRouteSegmentIndex();
  const isHotelsScreen = useIsHotelsUrlDeeplink();
  const {
    dateObject: { returnDate, departureDate },
  } = useTravelSearchSettings();

  return useMemo(() => {
    if (isHotelsScreen) {
      return {
        screenType: "hotel",
      };
    }

    if (!searchResponse || routeIndex === undefined) {
      return {
        screenType: "serp",
      };
    }

    const route = searchResponse.routes[routeIndex];
    const segment =
      routeSegmentIndex !== undefined
        ? searchResponse.segments[route.segments[routeSegmentIndex]]
        : undefined;

    const screenType = segment ? "segment" : "route";
    const screenModes: Mode[] = [];
    const routeDestinations = [];
    const carriers = carrierAdapter(
      searchResponse,
      routeIndex,
      routeSegmentIndex,
    ).map((carrier) => carrier.name);
    const travelPeriods = getTravelPeriods(departureDate, returnDate);

    if (routeSegmentIndex === undefined) {
      for (let i = 0; i < route.segments.length; i++) {
        const segmentIndex = route.segments[i];
        const isMajorSegment = searchResponse.segments[segmentIndex].isMajor;
        routeDestinations.push(
          destinationPlaceFromSegment(searchResponse, segmentIndex).shortName,
        );

        if (isMajorSegment) {
          const mode = transitModeFromSegment(searchResponse, routeIndex, i);

          if (!screenModes.includes(mode)) {
            screenModes.push(mode);
          }
        }
      }
    } else {
      const mode = transitModeFromSegment(
        searchResponse,
        routeIndex,
        routeSegmentIndex,
      );

      screenModes.push(mode);
    }

    return {
      screenType,
      screenModes,
      carriers,
      travelPeriods,
      ...(screenType === "route" && {
        routeDestinations,
      }),
    } satisfies RouteSegmentScreen;
  }, [
    isHotelsScreen,
    searchResponse,
    routeIndex,
    routeSegmentIndex,
    returnDate,
    departureDate,
  ]);
}

function getTravelPeriods(departureDate: string, arrivalDate?: string) {
  // A travel period is a month and year combination, eg: "March2025".
  const travelPeriods: string[] = [];
  const startDate = departureDate === "" ? new Date() : new Date(departureDate);

  if (arrivalDate) {
    const currentDate = startDate;
    currentDate.setDate(1);
    const endDate = new Date(arrivalDate);

    // Keep incrementing through each month until we reach the end date.
    while (currentDate <= endDate) {
      const currentPeriod = getMonthYear(currentDate);
      travelPeriods.push(currentPeriod);

      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  } else {
    const departurePeriod = getMonthYear(startDate);
    travelPeriods.push(departurePeriod);
  }

  return travelPeriods;
}

function getMonthYear(date: Date) {
  const monthYear = date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return monthYear.replace(" ", "");
}
